import { RunSummaryDto } from '../../../backend/src/matter/subset/dto/run-summary.dto';
import { RunType } from '../../../backend/src/run/run-type';
import { RunStatus } from '../../../backend/src/types/types';
import { MatterRun, Run } from '@/api/types/backend-dto-types';
import { RunWithSubsetName } from '@/new/components/breadcrumb';
import { isProcessingStatus } from '@/new/lib/pipeline-status';

/** Human-readable label for every run type, independent of the case it ran on. */
export const RunTypeLabels: Record<RunType, string> = {
  [RunType.Regular]: 'Regular',
  [RunType.AddDocs]: 'New Documents',
  [RunType.AddDocsUpdateSynopsis]: 'New Documents & Updated Details',
  [RunType.UpdateSynopsis]: 'Updated Matter Issues',
  [RunType.UpdateInconsistencies]: 'Updated Inconsistencies',
  [RunType.DeleteDocuments]: 'Deleted Documents',
  [RunType.Reprocess]: 'Reprocessing Run',
};

export const AllRunTypes = Object.keys(RunTypeLabels) as RunType[];

export const getRunType = (run: Run | RunSummaryDto | MatterRun, runOnAllDocsCase: boolean) => {
  // Subset runs recontextualise rather than update the matter issues, so this one type
  // reads differently depending on the case it ran on.
  if (run.type === RunType.UpdateSynopsis && !runOnAllDocsCase) {
    return 'Recontextualising Subset Facts';
  }
  return RunTypeLabels[run.type as RunType] ?? 'Unknown';
};

/**
 * Check if a run is ongoing (status is a processing status)
 */
export const isRunOngoing = (run: Run | RunWithSubsetName | MatterRun): boolean =>
  !!run.status && isProcessingStatus(run.status as RunStatus);

/**
 * Check if any run in an array is ongoing
 */
export const hasAnyOngoingRun = (runs: Run[] | RunWithSubsetName[] | MatterRun[] | undefined): boolean => {
  if (!runs || runs.length === 0) return false;
  return runs.some(isRunOngoing);
};

/**
 * Check if any runs are ongoing on the all docs case for a matter
 */
export const hasAnyOngoingRunOnAllDocsCase = (
  runs: Run[] | RunWithSubsetName[] | MatterRun[] | undefined,
  allDocsCaseId: string,
): boolean => {
  if (!runs || !Array.isArray(runs) || runs.length === 0) return false;
  return runs.some((run) => run.caseId === allDocsCaseId && isRunOngoing(run));
};

/**
 * Check if any runs are ongoing which modify the number of documents in the matter
 */
export const hasOngoingRunWhichModifiesDocumentCount = (
  runs: Run[] | RunWithSubsetName[] | MatterRun[] | undefined,
): boolean => {
  if (!runs || !Array.isArray(runs) || runs.length === 0) return false;
  return runs.some(
    (run) =>
      (run.type === RunType.AddDocs ||
        run.type === RunType.AddDocsUpdateSynopsis ||
        run.type === RunType.Regular ||
        run.type === RunType.DeleteDocuments) &&
      isRunOngoing(run),
  );
};

/**
 * Estimates use the current plan; an unavailable rate must not imply legacy pricing.
 * Rounded up to whole credits, so any run with pages estimates at least 1 credit.
 */
export const getRunCreditEstimate = (run: Pick<RunSummaryDto, 'totalPages' | 'pagesPerCredit'>): number | null =>
  run.pagesPerCredit > 0 ? Math.ceil(run.totalPages / run.pagesPerCredit) : null;

export const formatRunCredits = (credits: number): string => credits.toLocaleString('en-GB');
